/**
 * 더존 G20 기초이월관리 → ERP aslpfwd_dtl / aslpviw(GB=0) 엑셀 컨버전
 *
 * 운영 이관(sida_운영 generate_aslpfwd_dtl / generate_aslpviw_fwd)과 동일 규칙.
 * 원자료는 메모리만 두고, 그리드에는 화면 분량만 넘긴다.
 */
import * as XLSX from 'xlsx';

export const DEFAULT_PAGE_SIZE = 200;
export const CONVERT_CHUNK = 2000;
export const PAGE_SIZE_OPTIONS = [
  { value: '100', label: '100건' },
  { value: '200', label: '200건' },
  { value: '500', label: '500건' },
  { value: '1000', label: '1,000건' },
];

function yieldToUi() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}

function normalizeHeader(h) {
  return String(h || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[()[\]{}._\-/·]/g, '');
}

function toAmt(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  const n = Number(String(v).replace(/,/g, '').replace(/원/g, '').replace(/\s/g, ''));
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

export function sliceForView(rows, pageNo, pageSize) {
  const size = Math.max(50, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const total = (rows || []).length;
  const totalPages = Math.max(1, Math.ceil(total / size) || 1);
  const page = Math.min(Math.max(1, Number(pageNo) || 1), totalPages);
  const start = (page - 1) * size;
  return {
    viewRows: (rows || []).slice(start, start + size),
    page,
    totalPages,
    start,
    end: Math.min(start + size, total),
    size,
    total,
  };
}

const ACC_UNT = '1';
const MST_SEQ = 1;
const INS_USR = 'G20_MIG';
const BUD_DPT = '4120';
const PARSE_CHUNK = 800;

/** 한글 기초이월 시트 고정 열 (검증된 23~26년 양식) */
const KOR_COL = {
  accCd: 1,
  amt1: 4,
  amt2: 5,
  cusNms: 7,
  cusCd: 9,
  dptCd: 11,
  bizCd: 14,
  actNo: 17,
};

const store = {
  tenantCode: '',
  fileName: '',
  loadedAt: '',
  sourceRows: [],
  accMap: new Map(),
  parseWarnings: [],
  skipped: 0,
  sheetName: '',
  accSheet: '',
  layout: '',
};

export function getFwdStore() {
  return store;
}

export function clearFwdStore() {
  store.fileName = '';
  store.loadedAt = '';
  store.sourceRows = [];
  store.accMap = new Map();
  store.parseWarnings = [];
  store.skipped = 0;
  store.sheetName = '';
  store.accSheet = '';
  store.layout = '';
}

function cellStr(v) {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function bizNo(v) {
  return cellStr(v).replace(/[-\s]/g, '');
}

function toNumParen(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const s = String(v).trim().replace(/,/g, '').replace(/^\((.*)\)$/, '-$1');
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function inferYearFromName(fileName, sheetName) {
  const blob = `${fileName || ''} ${sheetName || ''}`;
  const y4 = blob.match(/(20\d{2})/);
  if (y4) return y4[1];
  const y2 = blob.match(/(?:^|[^\d])(\d{2})\s*년/);
  if (y2) return `20${y2[1]}`;
  return '';
}

function isKorHeader(row) {
  const joined = (row || []).map((c) => String(c).replace(/\s/g, '')).join('|');
  return joined.includes('*계정과목') && joined.includes('차변');
}

function isDumpHeader(headers) {
  const keys = (headers || []).map((h) => normalizeHeader(h));
  return keys.includes('acccd') && keys.includes('dtlamt1') && keys.includes('slpyy');
}

function findHeaderRow(aoa) {
  const rows = aoa || [];
  const limit = Math.min(rows.length, 20);
  for (let i = 0; i < limit; i += 1) {
    const row = rows[i] || [];
    if (isKorHeader(row) || isDumpHeader(row)) return i;
  }
  return 0;
}

function readWorkbook(buf) {
  return XLSX.read(buf, { type: 'array', dense: true, cellDates: true, raw: false });
}

function sheetToAoa(wb, name) {
  const ws = wb.Sheets[name];
  if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
}

function pickFwdSheet(wb) {
  const names = wb.SheetNames || [];
  const prefer = names.find((n) => /기초|이월|fwd|aslpfwd/i.test(String(n)));
  return prefer || names.find((n) => !/계정|aaccmst|accmst/i.test(String(n))) || names[0];
}

function pickAccSheet(wb) {
  const names = wb.SheetNames || [];
  return names.find((n) => /계정마스터|계정과목마스터|aaccmst|accmst/i.test(String(n))) || '';
}

export function levelsFromUpp(upp) {
  const s = String(upp || '').trim();
  const parts = s.split('-').filter(Boolean);
  const prefix = parts[0] || '';
  const base = {
    upp: s,
    lv1: prefix.slice(0, 1),
    lv2: prefix.slice(0, 2),
    lv3: prefix,
  };
  if (parts.length >= 3) {
    return {
      ...base,
      lv4: `${parts[0]}-${parts[1]}`,
      lv5: parts.slice(2).join('-'),
      lvEnd: '5',
    };
  }
  return { ...base, lv4: s, lv5: '', lvEnd: '4' };
}

function parseAccMap(aoa) {
  const map = new Map();
  if (!aoa?.length) return map;
  const { index, headers } = (() => {
    const limit = Math.min(aoa.length, 15);
    for (let i = 0; i < limit; i += 1) {
      const headers = (aoa[i] || []).map((c) => cellStr(c));
      const keys = headers.map((h) => normalizeHeader(h));
      if (keys.includes('acccd') || keys.includes('계정코드')) {
        return { index: i, headers };
      }
    }
    return { index: 0, headers: (aoa[0] || []).map((c) => cellStr(c)) };
  })();
  const idx = { cd: 0, upp: -1, dcr: -1, nm: -1 };
  headers.forEach((h, i) => {
    const k = normalizeHeader(h);
    if (['acccd', '계정코드', '계정과목코드'].includes(k)) idx.cd = i;
    if (['accuppcd', '상위코드', '계정상위', 'uppcd'].includes(k)) idx.upp = i;
    if (['accdcrgb', '차대구분', 'dcrgb'].includes(k)) idx.dcr = i;
    if (['accnm', '계정명', '계정과목명'].includes(k)) idx.nm = i;
  });
  for (let r = index + 1; r < aoa.length; r += 1) {
    const row = aoa[r] || [];
    const cd = cellStr(row[idx.cd]);
    if (!cd) continue;
    map.set(cd, {
      ACC_CD: cd,
      ACC_UPP_CD: idx.upp >= 0 ? cellStr(row[idx.upp]) : '',
      ACC_DCR_GB: idx.dcr >= 0 ? cellStr(row[idx.dcr]) : '1',
      ACC_NM: idx.nm >= 0 ? cellStr(row[idx.nm]) : '',
    });
  }
  return map;
}

function dumpColIndex(headers) {
  const col = {};
  (headers || []).forEach((h, i) => {
    const k = normalizeHeader(h);
    if (['entcd', '기관코드'].includes(k)) col.entCd = i;
    if (['slpyy', '회계연도', '연도'].includes(k)) col.slpYy = i;
    if (['mstseq'].includes(k)) col.mstSeq = i;
    if (['dtlseq', '순번'].includes(k)) col.dtlSeq = i;
    if (['accunt', '회계단위'].includes(k)) col.accUnt = i;
    if (['acccd', '계정코드', '*계정과목', '계정과목'].includes(k) && col.accCd == null) col.accCd = i;
    if (['dtlamt1', '차변'].includes(k)) col.amt1 = i;
    if (['dtlamt2', '대변'].includes(k)) col.amt2 = i;
    if (['cuscd', '사업자번호'].includes(k)) col.cusCd = i;
    if (['cusnms', '거래처', '거래처명'].includes(k) && col.cusNms == null) col.cusNms = i;
    if (['depnm'].includes(k)) col.depNm = i;
    if (['dptcd', '부서', '부서코드'].includes(k)) col.dptCd = i;
    if (['actseq'].includes(k)) col.actSeq = i;
    if (['actno', '계좌번호'].includes(k)) col.actNo = i;
    if (['bizcd', '세부사업', '사업코드'].includes(k)) col.bizCd = i;
  });
  return col;
}

function pushRow(list, row) {
  list.push(row);
}

export async function parseG20FwdWorkbook(buf, { tenantCode, fileName, fiscalYear, onProgress } = {}) {
  const wb = readWorkbook(buf);
  const sheetName = pickFwdSheet(wb);
  if (!sheetName) throw new Error('엑셀 시트를 찾지 못했습니다.');
  const aoa = sheetToAoa(wb, sheetName);
  if (!aoa.length) throw new Error('기초이월 시트가 비어 있습니다.');

  const headerIdx = findHeaderRow(aoa);
  const headerRow = aoa[headerIdx] || [];
  const kor = isKorHeader(headerRow);
  const dump = isDumpHeader(headerRow);
  if (!kor && !dump) {
    throw new Error('기초이월 헤더를 인식하지 못했습니다. *계정과목·차변 또는 ACC_CD·DTL_AMT1 양식을 사용하세요.');
  }

  const accSheet = pickAccSheet(wb);
  const accMap = accSheet ? parseAccMap(sheetToAoa(wb, accSheet)) : new Map();
  const fromFile = inferYearFromName(fileName, sheetName);
  const yearHint = fromFile || String(fiscalYear || '').trim();
  const dumpIdx = dump ? dumpColIndex(headerRow.map((c) => cellStr(c))) : null;

  const sourceRows = [];
  let skipped = 0;
  const warnings = [];
  if (!accMap.size) {
    warnings.push('계정마스터 시트가 없습니다. 재무제표(aslpviw) 계층코드는 비워 둡니다. 시트명을 계정마스터/AACCMST 로 넣으면 채워집니다.');
  }

  for (let r = headerIdx + 1; r < aoa.length; r += 1) {
    const row = aoa[r] || [];
    if (!(row || []).some((c) => cellStr(c) !== '')) continue;

    let accCd = '';
    let amt1 = 0;
    let amt2 = 0;
    let cusNms = '';
    let cusCd = '';
    let dptCd = '';
    let bizCd = '';
    let actNo = '';
    let slpYy = yearHint;
    let entCd = tenantCode || '';
    let mstSeq = MST_SEQ;
    let accUnt = ACC_UNT;
    let depNm = '';
    let actSeq = '';
    let dtlSeq = 0;

    if (kor) {
      accCd = cellStr(row[KOR_COL.accCd]);
      amt1 = toNumParen(row[KOR_COL.amt1]);
      amt2 = toNumParen(row[KOR_COL.amt2]);
      cusNms = cellStr(row[KOR_COL.cusNms]);
      cusCd = bizNo(row[KOR_COL.cusCd]);
      dptCd = cellStr(row[KOR_COL.dptCd]);
      bizCd = cellStr(row[KOR_COL.bizCd]);
      actNo = cellStr(row[KOR_COL.actNo]);
    } else {
      const take = (key) => (dumpIdx[key] == null ? '' : row[dumpIdx[key]]);
      accCd = cellStr(take('accCd'));
      amt1 = toNumParen(take('amt1'));
      amt2 = toNumParen(take('amt2'));
      cusNms = cellStr(take('cusNms'));
      cusCd = bizNo(take('cusCd'));
      dptCd = cellStr(take('dptCd'));
      bizCd = cellStr(take('bizCd'));
      actNo = cellStr(take('actNo'));
      slpYy = cellStr(take('slpYy')) || slpYy;
      entCd = cellStr(take('entCd')) || entCd;
      mstSeq = Number(take('mstSeq')) || MST_SEQ;
      accUnt = cellStr(take('accUnt')) || ACC_UNT;
      depNm = cellStr(take('depNm'));
      actSeq = cellStr(take('actSeq'));
      dtlSeq = Number(take('dtlSeq')) || 0;
    }

    if (!accCd) {
      if (amt1 || amt2) skipped += 1;
      continue;
    }

    pushRow(sourceRows, {
      _rowUid: `FWD-${r + 1}`,
      srcRow: r + 1,
      tenantCode: entCd,
      slpYy,
      mstSeq,
      accUnt,
      accCd,
      acctNm: accMap.get(accCd)?.ACC_NM || '',
      amt1: toAmt(amt1) || Math.trunc(amt1),
      amt2: toAmt(amt2) || Math.trunc(amt2),
      cusCd,
      cusNms,
      depNm,
      dptCd,
      actSeq,
      actNo,
      bizCd,
      dtlSeq,
    });

    if (sourceRows.length % PARSE_CHUNK === 0) {
      onProgress?.({ loaded: sourceRows.length, skipped, totalHint: aoa.length - headerIdx - 1 });
      await yieldToUi();
    }
  }

  if (!sourceRows.length) {
    throw new Error('계정코드가 있는 기초이월 행이 없습니다.');
  }

  const years = [...new Set(sourceRows.map((r) => r.slpYy).filter((y) => /^\d{4}$/.test(y)))];
  if (!years.length) {
    warnings.push('회계연도를 엑셀에서 찾지 못했습니다. 조회조건 회계연도를 지정한 뒤 변환하세요.');
  }

  store.tenantCode = tenantCode || '';
  store.fileName = fileName || '';
  store.loadedAt = new Date().toISOString();
  store.sourceRows = sourceRows;
  store.accMap = accMap;
  store.parseWarnings = warnings;
  store.skipped = skipped;
  store.sheetName = sheetName;
  store.accSheet = accSheet || '';
  store.layout = kor ? 'kor' : 'dump';

  return {
    fileName: store.fileName,
    sheetName,
    accSheet,
    layout: store.layout,
    rowCount: sourceRows.length,
    skipped,
    mapCount: accMap.size,
    years,
    warnings,
  };
}

export function filterFwdRows(rows, values = {}) {
  const year = String(values.fiscalYear || '').trim();
  const q = String(values.keyword || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (year && String(r.slpYy || '') !== year) return false;
    if (q) {
      const blob = [r.accCd, r.acctNm, r.cusNms, r.cusCd, r.dptCd, r.bizCd, r.actNo].join(' ').toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });
}

export async function convertFwdRows(sourceRows, { tenantCode, fiscalYear, accMap, onProgress } = {}) {
  const map = accMap instanceof Map ? accMap : store.accMap;
  const yearHint = String(fiscalYear || '').trim();
  const dtl = [];
  const viw = [];
  const missingAcc = [];
  let sumDr = 0;
  let sumCr = 0;
  let seq = 0;

  for (let i = 0; i < (sourceRows || []).length; i += 1) {
    const src = sourceRows[i];
    const yy = String(src.slpYy || yearHint || '').trim();
    if (!/^\d{4}$/.test(yy)) {
      throw new Error('회계연도가 없습니다. 조회조건에서 회계연도를 선택한 뒤 변환하세요.');
    }
    seq += 1;
    const dtlSeq = Number(src.dtlSeq) || seq;
    const accCd = String(src.accCd || '').trim();
    const amt1 = toAmt(src.amt1);
    const amt2 = toAmt(src.amt2);
    sumDr += amt1;
    sumCr += amt2;
    const entCd = tenantCode || src.tenantCode || '';
    const dptCd = cellStr(src.dptCd);
    const acc = map.get(accCd);

    dtl.push({
      ENT_CD: entCd,
      SLP_YY: yy,
      MST_SEQ: Number(src.mstSeq) || MST_SEQ,
      DTL_SEQ: dtlSeq,
      ACC_UNT: src.accUnt || ACC_UNT,
      ACC_CD: accCd,
      DTL_AMT1: amt1,
      DTL_AMT2: amt2,
      CUS_CD: src.cusCd || '',
      CUS_NMS: src.cusNms || '',
      DEP_NM: src.depNm || '',
      DPT_CD: dptCd,
      ACT_SEQ: src.actSeq || '',
      ACT_NO: src.actNo || '',
      BIZ_CD: src.bizCd || '',
      INS_USR,
    });

    const lv = levelsFromUpp(acc?.ACC_UPP_CD || '');
    if (!acc) missingAcc.push(accCd);
    viw.push({
      ENT_CD: entCd,
      SLP_YY: yy,
      SLP_MM: '1',
      GB: '0',
      SLP_SEQ: Number(src.mstSeq) || MST_SEQ,
      DTL_SEQ: dtlSeq,
      SLP_GB: '',
      RES_SEQ: '',
      RES_SUB_SEQ: '',
      RES_SEQ2: '',
      SLP_TTL: '',
      ACC_UNT: src.accUnt || ACC_UNT,
      BIZ_CD: src.bizCd || '',
      BUD_DPT: dptCd || BUD_DPT,
      DPT_CD: dptCd,
      CNF_YMD: `${yy}0100`,
      ACC_CD: accCd,
      ACC_UPP_CD: lv.upp,
      LV1: lv.lv1,
      LV2: lv.lv2,
      LV3: lv.lv3,
      LV4: lv.lv4,
      LV5: lv.lv5,
      LV_END: lv.lvEnd,
      ACC_DCR_GB: acc?.ACC_DCR_GB || '',
      DTL_AMT1: amt1,
      DTL_AMT2: amt2,
      CUS_NMS: src.cusNms || '',
      CUS_CD: src.cusCd || '',
      DEP_NM: src.depNm || '',
      ACT_SEQ: src.actSeq || '',
    });

    if ((i + 1) % CONVERT_CHUNK === 0) {
      onProgress?.({ done: i + 1, total: sourceRows.length });
      await yieldToUi();
    }
  }

  const missingUniq = [...new Set(missingAcc)];
  return {
    dtl,
    viw,
    summary: {
      rowCnt: dtl.length,
      sumDr,
      sumCr,
      balanced: Math.abs(sumDr - sumCr) <= 0.5,
      diffAmt: Math.round((sumCr - sumDr) * 100) / 100,
      missingAccCnt: missingUniq.length,
      missingAcc: missingUniq,
      accMapCnt: map.size,
    },
  };
}

const DTL_EXPORT_COLS = [
  ['ENT_CD', 'ENT_CD'],
  ['SLP_YY', 'SLP_YY'],
  ['MST_SEQ', 'MST_SEQ'],
  ['DTL_SEQ', 'DTL_SEQ'],
  ['ACC_UNT', 'ACC_UNT'],
  ['ACC_CD', 'ACC_CD'],
  ['DTL_AMT1', 'DTL_AMT1'],
  ['DTL_AMT2', 'DTL_AMT2'],
  ['CUS_CD', 'CUS_CD'],
  ['CUS_NMS', 'CUS_NMS'],
  ['DEP_NM', 'DEP_NM'],
  ['DPT_CD', 'DPT_CD'],
  ['ACT_SEQ', 'ACT_SEQ'],
  ['ACT_NO', 'ACT_NO'],
  ['BIZ_CD', 'BIZ_CD'],
  ['INS_USR', 'INS_USR'],
];

const VIW_EXPORT_COLS = [
  ['ENT_CD', 'ENT_CD'],
  ['SLP_YY', 'SLP_YY'],
  ['SLP_MM', 'SLP_MM'],
  ['GB', 'GB'],
  ['SLP_SEQ', 'SLP_SEQ'],
  ['DTL_SEQ', 'DTL_SEQ'],
  ['SLP_GB', 'SLP_GB'],
  ['ACC_UNT', 'ACC_UNT'],
  ['BIZ_CD', 'BIZ_CD'],
  ['BUD_DPT', 'BUD_DPT'],
  ['DPT_CD', 'DPT_CD'],
  ['CNF_YMD', 'CNF_YMD'],
  ['ACC_CD', 'ACC_CD'],
  ['ACC_UPP_CD', 'ACC_UPP_CD'],
  ['LV1', 'LV1'],
  ['LV2', 'LV2'],
  ['LV3', 'LV3'],
  ['LV4', 'LV4'],
  ['LV5', 'LV5'],
  ['LV_END', 'LV_END'],
  ['ACC_DCR_GB', 'ACC_DCR_GB'],
  ['DTL_AMT1', 'DTL_AMT1'],
  ['DTL_AMT2', 'DTL_AMT2'],
  ['CUS_NMS', 'CUS_NMS'],
  ['CUS_CD', 'CUS_CD'],
  ['DEP_NM', 'DEP_NM'],
  ['ACT_SEQ', 'ACT_SEQ'],
];

async function aoaFromRows(rows, cols, onProgress) {
  const aoa = [cols.map((c) => c[1])];
  for (let i = 0; i < rows.length; i += 1) {
    aoa.push(cols.map(([key]) => rows[i][key] ?? ''));
    if ((i + 1) % CONVERT_CHUNK === 0) {
      onProgress?.({ done: i + 1, total: rows.length });
      await yieldToUi();
    }
  }
  return aoa;
}

export async function downloadFwdWorkbook(converted, fileName, onProgress) {
  const wb = XLSX.utils.book_new();
  const dtlAoa = await aoaFromRows(converted.dtl, DTL_EXPORT_COLS, onProgress);
  const viwAoa = await aoaFromRows(converted.viw, VIW_EXPORT_COLS);
  const s = converted.summary;
  const summaryAoa = [
    ['항목', '값'],
    ['기초이월건수', s.rowCnt],
    ['차변합계', s.sumDr],
    ['대변합계', s.sumCr],
    ['차액(대-차)', s.diffAmt],
    ['차대평형', s.balanced ? 'Y' : 'N'],
    ['계정마스터건수', s.accMapCnt],
    ['계정미매핑', s.missingAccCnt],
    ['원본파일', store.fileName],
    ['변환시각', new Date().toISOString()],
    ['비고', 'aslpfwd_dtl 적재 후 aslpviw GB=0. 합계행(계정코드 없음)은 제외'],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(dtlAoa), 'aslpfwd_dtl');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(viwAoa), 'aslpviw');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryAoa), '변환요약');
  if (s.missingAccCnt) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([['ACC_CD'], ...s.missingAcc.map((c) => [c])]),
      '계정미매핑'
    );
  }
  const name = String(fileName || 'G20기초이월_ERP.xlsx');
  XLSX.writeFile(wb, name.endsWith('.xlsx') ? name : `${name}.xlsx`, { compression: true });
}

export function downloadG20FwdTemplate(fileName = 'G20기초이월_업로드양식.xlsx') {
  const wb = XLSX.utils.book_new();
  const hdr = [
    'No',
    '*계정과목',
    '계정과목명',
    '',
    '차변',
    '대변',
    '',
    '거래처',
    '거래처(G20)',
    '사업자번호',
    '',
    '부서',
    '',
    '',
    '세부사업',
    '',
    '',
    '계좌번호',
  ];
  const sample = [
    1,
    '11100',
    '보통예금',
    '',
    1000000,
    0,
    '',
    '우리은행',
    '',
    '1234567890',
    '',
    '4120',
    '',
    '',
    '',
    '',
    '',
    '1002-000-000000',
  ];
  const sample2 = [
    2,
    '31000',
    '기본금',
    '',
    0,
    1000000,
    '',
    '',
    '',
    '',
    '',
    '4120',
    '',
    '',
    '',
    '',
    '',
    '',
  ];
  const accAoa = [
    ['ACC_CD', 'ACC_NM', 'ACC_UPP_CD', 'ACC_DCR_GB'],
    ['11100', '보통예금', '111-01', '1'],
    ['31000', '기본금', '310', '2'],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, sample, sample2]), '기초이월');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(accAoa), '계정마스터');
  XLSX.writeFile(wb, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
}

export function sourceFwdGridColumns(hiCol) {
  return [
    hiCol.roTextColumn({ header: '회계연도', field: 'slpYy', size: 90 }),
    hiCol.roTextColumn({ header: '계정코드', field: 'accCd', size: 100 }),
    hiCol.roTextColumn({ header: '계정명', field: 'acctNm', size: 140 }),
    hiCol.roNumberColumn({ header: '차변', field: 'amt1', size: 120 }),
    hiCol.roNumberColumn({ header: '대변', field: 'amt2', size: 120 }),
    hiCol.roTextColumn({ header: '거래처', field: 'cusNms', size: 140 }),
    hiCol.roTextColumn({ header: '사업자번호', field: 'cusCd', size: 120 }),
    hiCol.roTextColumn({ header: '부서', field: 'dptCd', size: 90 }),
    hiCol.roTextColumn({ header: '세부사업', field: 'bizCd', size: 100 }),
    hiCol.roTextColumn({ header: '계좌번호', field: 'actNo', size: 140 }),
  ];
}
